import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Button, Form, Spinner, Alert, Pagination } from "react-bootstrap";
import { FaArrowLeft, FaBookOpen, FaCheckCircle, FaRegCircle, FaLanguage, FaLayerGroup, FaUserGraduate, FaRoute } from "react-icons/fa";
import apiService from "../../services/ApiServices";
import "./AdminLearningJourney.css";

const LANGUAGE_OPTIONS = [
  { value: "english", label: "English" },
  { value: "hindi", label: "Hindi" },

];

const LEVEL_OPTIONS = [
  { value: "1", label: "N5" },
  { value: "2", label: "N4" },
  { value: "3", label: "N3" },
  { value: "4", label: "N2" },
  { value: "5", label: "N1" },
];

const AdminLearningJourney = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const routeUser = location.state?.user || null;

  const selectedUser = useMemo(() => {
    const userId = queryParams.get("user_id") || routeUser?.user_id || "";
    const studentId = queryParams.get("student_id") || routeUser?.student_id || userId;

    return {
      user_id: userId,
      student_id: studentId,
      name: queryParams.get("name") || routeUser?.name || routeUser?.user_name || "Student",
      email: queryParams.get("email") || routeUser?.email || "",
      college_id: queryParams.get("college_id") || routeUser?.college_id || "",
    };
  }, [queryParams, routeUser]);

  const [language, setLanguage] = useState("english");
  const [level, setLevel] = useState("2");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [roadmap, setRoadmap] = useState(null);
  const [visited, setVisited] = useState([]);
  const [lessonFilter, setLessonFilter] = useState("all");
  const [lessonSearch, setLessonSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [collapsedDays, setCollapsedDays] = useState({});
  const ITEMS_PER_PAGE = 10;

  const selectedLevelLabel = LEVEL_OPTIONS.find((item) => item.value === level)?.label || `Level ${level}`;

  const normalizedRoadmapDays = useMemo(() => roadmap?.days || [], [roadmap]);

  const getLessonLabel = (day) => day.lesson || day.dataValues?.day_name || day.day_name || `Lesson ${day.day_id || day.id}`;

  const lessonOptions = useMemo(() => {
    const lessons = [];
    normalizedRoadmapDays.forEach((day) => {
      const lesson = getLessonLabel(day);
      if (!lessons.includes(lesson)) lessons.push(lesson);
    });
    return lessons;
  }, [normalizedRoadmapDays]);

  const filteredLessonOptions = useMemo(() => {
    const query = lessonSearch.trim().toLowerCase();
    if (!query) return lessonOptions;
    return lessonOptions.filter((lesson) => lesson.toLowerCase().includes(query));
  }, [lessonOptions, lessonSearch]);

  const filteredRoadmapDays = useMemo(() => {
    if (lessonFilter === "all") return normalizedRoadmapDays;
    return normalizedRoadmapDays.filter((day) => getLessonLabel(day) === lessonFilter);
  }, [normalizedRoadmapDays, lessonFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRoadmapDays.length / ITEMS_PER_PAGE));
  const paginatedRoadmapDays = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredRoadmapDays.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredRoadmapDays, currentPage]);

  const visitedDaySet = useMemo(() => {
    return new Set(
      visited
        .map((item) => Number(item.unique_day_id || item.lessonDetails?.day_id || item.id))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
  }, [visited]);

  const visitedMethodSet = useMemo(() => {
    return new Set(
      visited
        .map((item) => Number(item.learning_method_id || item.learningMethodDetails?.learning_method_id))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
  }, [visited]);

  const visitedScreenSet = useMemo(() => {
    return new Set(
      visited
        .map((item) => Number(item.screen_title_id || item.sectionDetails?.screen_title_id))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
  }, [visited]);

  const visitedDayMeta = useMemo(() => {
    const map = new Map();
    visited.forEach((item) => {
      const dayId = Number(item.unique_day_id || item.lessonDetails?.day_id || item.id);
      if (!Number.isFinite(dayId) || dayId <= 0) return;
      const visitedAt = item.visitedAt || item.visited_at || item.createdAt || "";
      const current = map.get(dayId);
      if (!current || new Date(visitedAt).getTime() > new Date(current).getTime()) {
        map.set(dayId, visitedAt);
      }
    });
    return map;
  }, [visited]);

  const visitedMethodMeta = useMemo(() => {
    const map = new Map();
    visited.forEach((item) => {
      const methodId = Number(item.learning_method_id || item.learningMethodDetails?.learning_method_id);
      if (!Number.isFinite(methodId) || methodId <= 0) return;
      const visitedAt = item.visitedAt || item.visited_at || item.createdAt || "";
      const current = map.get(methodId);
      if (!current || new Date(visitedAt).getTime() > new Date(current).getTime()) {
        map.set(methodId, visitedAt);
      }
    });
    return map;
  }, [visited]);

  const visitedScreenMeta = useMemo(() => {
    const map = new Map();
    visited.forEach((item) => {
      const screenId = Number(item.screen_title_id || item.sectionDetails?.screen_title_id);
      if (!Number.isFinite(screenId) || screenId <= 0) return;
      const visitedAt = item.visitedAt || item.visited_at || item.createdAt || "";
      const current = map.get(screenId);
      if (!current || new Date(visitedAt).getTime() > new Date(current).getTime()) {
        map.set(screenId, visitedAt);
      }
    });
    return map;
  }, [visited]);

  useEffect(() => {
    setCurrentPage(1);
  }, [lessonFilter, roadmap]);

  useEffect(() => {
    setCollapsedDays({});
  }, [roadmap, lessonFilter, currentPage]);

  useEffect(() => {
    if (roadmap) {
      setLessonFilter((current) => (current === "all" ? current : "all"));
    }
  }, [roadmap]);

  useEffect(() => {
    if (!filteredLessonOptions.includes(lessonFilter) && lessonFilter !== "all") {
      setLessonFilter("all");
    }
  }, [filteredLessonOptions, lessonFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleLoadJourney = async () => {
    if (!selectedUser?.student_id && !selectedUser?.user_id) {
      setError("Student information missing. Please open this page from Admin Chat.");
      return;
    }

    if (!language || !level) {
      setError("Please select both language and level.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const studentId = Number(selectedUser.student_id || selectedUser.user_id);
      const examLevel = Number(level);

      const [roadmapResponse, visitResponse] = await Promise.all([
        apiService.get(`/api/exam-types/days/${examLevel}/roadmap`, { language }),
        apiService.get("/api/user-visits/all-visited-status", {
          user_id: studentId,
          examId: examLevel,
          language,
        }),
      ]);

      if (!roadmapResponse?.success) {
        throw new Error("Failed to load roadmap data.");
      }

      setRoadmap({
        level: roadmapResponse?.message?.level || selectedLevelLabel,
        examId: roadmapResponse?.message?.examId || examLevel,
        days: Array.isArray(roadmapResponse?.message?.days) ? roadmapResponse.message.days : [],
      });

      setVisited(Array.isArray(visitResponse?.message?.data) ? visitResponse.message.data : []);
    } catch (err) {
      setRoadmap(null);
      setVisited([]);
      setError(err?.response?.data?.message || err.message || "Failed to load learning journey.");
    } finally {
      setLoading(false);
    }
  };

  const isDayLearned = (day) => {
    const dayId = Number(day.day_id || day.dataValues?.day_id || day.id);
    return visitedDaySet.has(dayId);
  };

  const isMethodLearned = (method) => {
    return visitedMethodSet.has(Number(method.learning_method_id));
  };

  const isScreenLearned = (screen) => {
    return visitedScreenSet.has(Number(screen.screen_title_id));
  };

  const formatVisitedAt = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
      timeZoneName: "short",
    })
      .format(date)
      .replace("GMT+5:30", "IST");
  };

  const toggleDayCollapsed = (dayId) => {
    setCollapsedDays((prev) => ({
      ...prev,
      [dayId]: !prev[dayId],
    }));
  };

  return (
    <Container fluid className="py-4 learning-journey-page">
      <div className="learning-journey-shell mb-4">
        <div className="learning-journey-shell__top">
          <div>
            <Button variant="outline-secondary" className="learning-journey-back" onClick={() => navigate(-1)}>
              <FaArrowLeft className="me-2" />
              Back
            </Button>
            <div className="learning-journey-hero__eyebrow">Learning Journey</div>
            <h2 className="mb-2">
              <FaRoute className="me-2" />
              Student progress
            </h2>
            <p className="mb-0">
              See lesson-wise progress, then open a lesson to check what is learned and what is still pending.
            </p>
          </div>

          <Card className="learning-journey-student-card">
            <Card.Body>
              <div className="text-muted small mb-1">Selected Student</div>
              <div className="learning-journey-student-name">{selectedUser.name}</div>
              {selectedUser.email && <div className="text-muted small">{selectedUser.email}</div>}
              <div className="learning-journey-student-meta">
                <span><FaUserGraduate className="me-1" /> {selectedUser.student_id || selectedUser.user_id || "N/A"}</span>
                <span><FaLayerGroup className="me-1" /> {selectedLevelLabel}</span>
              </div>
            </Card.Body>
          </Card>
        </div>

        {error && (
          <Alert variant="danger" className="mb-0 mt-4">
            {error}
          </Alert>
        )}

        <Card className="learning-journey-filters mt-4 border-0 shadow-sm">
          <Card.Body>
            <Row className="g-3 align-items-end">
              <Col lg={4} md={6}>
                <Form.Label className="learning-journey-label">
                  <FaLanguage className="me-2" />
                  Language
                </Form.Label>
                <Form.Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                  {LANGUAGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col lg={4} md={6}>
                <Form.Label className="learning-journey-label">
                  <FaBookOpen className="me-2" />
                  Level
                </Form.Label>
                <Form.Select value={level} onChange={(e) => setLevel(e.target.value)}>
                  {LEVEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col lg={4} md={12} className="d-grid">
                <Button variant="primary" className="learning-journey-load-btn" onClick={handleLoadJourney} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    "Load Journey"
                  )}
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {roadmap ? (
          <>
            <Card className="learning-journey-lesson-filter mt-4 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
                  <div>
                    <div className="learning-journey-section-label">Lesson Filter</div>
                    <h5 className="mb-1">Choose a lesson</h5>
                    <div className="text-muted small">Filter the list by a single lesson to keep the view simple.</div>
                  </div>
                  <div className="learning-journey-lesson-filter__control">
                    <Form.Control
                      className="mb-2"
                      placeholder="Search lesson..."
                      value={lessonSearch}
                      onChange={(e) => setLessonSearch(e.target.value)}
                    />
                    <Form.Select value={lessonFilter} onChange={(e) => setLessonFilter(e.target.value)} disabled={!roadmap}>
                      <option value="all">All Lessons</option>
                      {filteredLessonOptions.map((lesson) => (
                        <option key={lesson} value={lesson}>
                          {lesson}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {filteredRoadmapDays.length === 0 ? (
              <Card className="text-center p-5 border-0 shadow-sm mt-4">
                <h5 className="mb-2">No lessons found</h5>
                <div className="text-muted">Try a different lesson filter.</div>
              </Card>
            ) : (
              <>
                <div className="learning-journey-list mt-4">
                  {paginatedRoadmapDays.map((day) => {
                    const dayId = day.day_id || day.dataValues?.day_id || day.id;
                    const dayLearned = isDayLearned(day);
                    const dayName = day.lesson || day.dataValues?.day_name || day.day_name || `Lesson ${dayId}`;
                    const dayDesc = day.dataValues?.desc || day.desc || "";
                    const methods = day.learningMethods || [];
                    const isCollapsed = collapsedDays[dayId] ?? true;

                    return (
                      <Card key={dayId} className={`learning-journey-day-card ${dayLearned ? "is-learned" : "is-not-learned"}`}>
                        <Card.Body>
                          <div className="learning-journey-day-summary">
                            <div>
                              <div className="learning-journey-day-label">Day {day.dataValues?.pono || dayId}</div>
                              <h5 className="mb-1">{dayName}</h5>
                              <div className="text-muted small">{dayDesc}</div>
                            </div>

                            <div className="learning-journey-day-summary__meta">
                              <span className={`learning-journey-status-pill ${dayLearned ? "is-learned" : "is-not-learned"}`}>
                                {dayLearned ? <FaCheckCircle className="me-1" /> : <FaRegCircle className="me-1" />}
                                {dayLearned ? "Learned" : "Not Learned"}
                              </span>
                              {dayLearned && visitedDayMeta.get(Number(dayId)) && (
                                <div className="learning-journey-visited-at">
                                  Visited at: {formatVisitedAt(visitedDayMeta.get(Number(dayId)))}
                                </div>
                              )}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="learning-journey-collapse-btn"
                                onClick={() => toggleDayCollapsed(dayId)}
                              >
                                {isCollapsed ? "Expand" : "Collapse"}
                              </Button>
                            </div>
                          </div>

                          {!isCollapsed && (
                          <div className="learning-journey-day-details">
                            <div className="learning-journey-method-list">
                              {methods.length === 0 ? (
                                <div className="text-muted small">No methods available for this lesson.</div>
                              ) : (
                                methods.map((method) => {
                                  const methodLearned = isMethodLearned(method);
                                  const screenTitles = method.screenTitles || [];

                                  return (
                                    <div key={method.learning_method_id} className="learning-journey-method-item">
                                      <div className="learning-journey-method-item__body">
                                        <div className="learning-journey-method-item__left">
                                          <div className="learning-journey-method-label">Learning Method</div>
                                          <div className="learning-journey-method-title">{method.method_type}</div>
                                          <div className="text-muted small">{method.desc}</div>
                                          <span className={`learning-journey-status-pill ${methodLearned ? "is-learned" : "is-not-learned"}`}>
                                            {methodLearned ? <FaCheckCircle className="me-1" /> : <FaRegCircle className="me-1" />}
                                            {methodLearned ? "Learned" : "Not Learned"}
                                          </span>
                                          {methodLearned && visitedMethodMeta.get(Number(method.learning_method_id)) && (
                                            <div className="learning-journey-visited-at learning-journey-visited-at--compact">
                                              Visited at: {formatVisitedAt(visitedMethodMeta.get(Number(method.learning_method_id)))}
                                            </div>
                                          )}
                                        </div>
                                        <div className="learning-journey-method-item__right">
                                          {screenTitles.length > 0 ? (
                                            <div className="learning-journey-screen-list">
                                              {screenTitles.map((screen) => {
                                                const screenLearned = isScreenLearned(screen);
                                                const screenVisitedAt = visitedScreenMeta.get(Number(screen.screen_title_id));
                                                return (
                                                  <div key={screen.screen_title_id} className={`learning-journey-screen-chip ${screenLearned ? "is-learned" : "is-not-learned"}`}>
                                                    <div className="learning-journey-screen-chip__title">{screen.title}</div>
                                                    <div className="learning-journey-screen-chip__subtitle">{screen.subtitle}</div>
                                                    <div className="learning-journey-screen-chip__status">
                                                      {screenLearned ? "Learned" : "Not Learned"}
                                                    </div>
                                                    {screenLearned && screenVisitedAt && (
                                                      <div className="learning-journey-screen-chip__visited">
                                                        {formatVisitedAt(screenVisitedAt)}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <div className="text-muted small">No sections available.</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                          )}
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="learning-journey-pagination-wrap">
                    <div className="text-muted small">
                      Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(currentPage * ITEMS_PER_PAGE, filteredRoadmapDays.length)} of {filteredRoadmapDays.length}
                    </div>

                    <Pagination className="learning-journey-pagination mb-0">
                      <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                      <Pagination.Prev
                        onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                        disabled={currentPage === 1}
                      />

                      {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => {
                        const shouldShow =
                          totalPages <= 5 ||
                          page === 1 ||
                          page === totalPages ||
                          Math.abs(page - currentPage) <= 1;

                        if (!shouldShow) return null;

                        return (
                          <Pagination.Item key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                            {page}
                          </Pagination.Item>
                        );
                      })}

                      <Pagination.Next
                        onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                        disabled={currentPage === totalPages}
                      />
                      <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          !loading && (
            <Card className="text-center p-5 border-0 shadow-sm mt-4">
              <h5 className="mb-2">Load learning journey</h5>
              <div className="text-muted">
                Choose a language and level, then click load to see lesson-wise progress.
              </div>
            </Card>
          )
        )}
      </div>
    </Container>
  );
};

export default AdminLearningJourney;
